import { useEffect, useState } from 'react';
import { useAuthStore } from './store/auth.store';
import type { TokenResponse } from './types/auth.types';
import LoginPage from './features/auth/components/LoginPage';
import { AppShell } from './components/layout/AppShell';
import { decodeJWT } from './lib/utils/jwt';

function App() {
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const [initialError, setInitialError] = useState<string>('');

  useEffect(() => {
    // 1. Check query parameters (?access_token=... or ?token=...)
    const params = new URLSearchParams(window.location.search);
    let tokenParam = params.get('access_token') || params.get('token');
    let refreshParam = params.get('refresh_token') || '';

    // 2. Check hash fragment (#accessToken=...&refreshToken=... or #error=...)
    const hash = window.location.hash.replace(/^#/, '');
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      const err = hashParams.get('error');
      if (err) {
        setInitialError(decodeURIComponent(err));
      }
      const hashAccessToken = hashParams.get('accessToken') || hashParams.get('access_token');
      const hashRefreshToken = hashParams.get('refreshToken') || hashParams.get('refresh_token');
      if (hashAccessToken) {
        tokenParam = hashAccessToken;
        refreshParam = hashRefreshToken || '';
      }
    }

    if (tokenParam) {
      useAuthStore.getState().applyRefreshedTokens(tokenParam, refreshParam);
      try {
        const decodedUser = decodeJWT(tokenParam);
        useAuthStore.setState({
          user: decodedUser,
          accessToken: tokenParam,
          refreshToken: refreshParam,
          isAuthenticated: true,
        });
      } catch (err) {
        console.error('Failed to parse handoff token', err);
      }
      const targetPath = window.location.pathname.startsWith('/sso/callback') ? '/' : window.location.pathname;
      try {
        window.history?.replaceState?.({}, document.title, targetPath);
      } catch (_) {}
    }

    const handleRefresh = (event: Event) => {
      const tokens = (event as CustomEvent<TokenResponse>).detail;
      useAuthStore
        .getState()
        .applyRefreshedTokens(tokens.access_token, tokens.refresh_token);
    };
    const handleExpiry = () => useAuthStore.getState().clearAuth();
    window.addEventListener('klara:auth-refreshed', handleRefresh);
    window.addEventListener('klara:auth-expired', handleExpiry);
    return () => {
      window.removeEventListener('klara:auth-refreshed', handleRefresh);
      window.removeEventListener('klara:auth-expired', handleExpiry);
    };
  }, []);

  const handleAuth = async (credentials: { tenantCode: string; email: string; password: string }) => {
    await login(credentials.email, credentials.password, credentials.tenantCode);
  };

  if (!user) {
    return <LoginPage onAuth={handleAuth} initialError={initialError} />;
  }

  return <AppShell />;
}

export default App;
